import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

const TIER_COLORS = {
  essential: { bg: "#E8DFC9", fg: "#5A4A20", accent: "#8A7635" },
  plus: { bg: BRAND_COLORS.navy, fg: BRAND_COLORS.ivory, accent: BRAND_COLORS.gold },
  signature: { bg: BRAND_COLORS.navyDark, fg: BRAND_COLORS.gold, accent: BRAND_COLORS.gold },
};

const TIER_LABEL_DE = {
  essential: "Service Paket 1",
  plus: "Service Paket 2",
  signature: "Service Paket 3",
};
const TIER_LABEL_EN = {
  essential: "Service Package 1",
  plus: "Service Package 2",
  signature: "Service Package 3",
};

export function MembershipBadge({ tier, size = "md" }) {
  const { lang } = useI18n();
  const de = lang !== "en";
  if (!tier) return null;
  const colors = TIER_COLORS[tier] || TIER_COLORS.essential;
  const label = (de ? TIER_LABEL_DE : TIER_LABEL_EN)[tier] || tier;
  const cls = size === "sm"
    ? "px-2.5 py-1 text-[10px] tracking-[0.18em]"
    : "px-3.5 py-1.5 text-[11px] tracking-[0.2em]";
  return (
    <span
      data-testid={`membership-badge-${tier}`}
      className={`inline-flex items-center gap-2 uppercase font-medium ${cls}`}
      style={{ background: colors.bg, color: colors.fg, borderLeft: `2px solid ${colors.accent}` }}
    >
      <span>{label}</span>
    </span>
  );
}

/**
 * Button + modal — reveals the list of services included in the customer's
 * assigned membership tier WITHOUT ever showing the tier name/number.
 * The tier itself is an internal driver; the customer only sees their services.
 */
export function MyServicesPanel() {
  const { lang } = useI18n();
  const de = lang !== "en";
  const [tierDetail, setTierDetail] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [me, catalog] = await Promise.all([
          api.get("/auth/me"),
          api.get("/memberships"),
        ]);
        if (cancelled) return;
        const tier = me.data?.membership_tier;
        const match = (catalog.data || []).find((t) => t.key === tier);
        setTierDetail(match || null);
      } catch (e) { console.debug("services panel load skipped:", e?.response?.status); }
    })();
    return () => { cancelled = true; };
  }, []);

  const groups = de ? tierDetail?.features_grouped_de : (tierDetail?.features_grouped_en || tierDetail?.features_grouped_de);
  const flat = de ? tierDetail?.features_de : (tierDetail?.features_en || tierDetail?.features_de);
  if (!groups && (!flat || flat.length === 0)) return null;

  let running = 0;

  return (
    <>
      <button
        type="button"
        data-testid="my-services-button"
        onClick={() => setOpen(true)}
        className="w-full bg-white border border-navy/10 hover:border-gold transition-colors p-6 flex items-center justify-between group text-left"
      >
        <div>
          <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>{de ? "Zum Nachlesen" : "For reference"}</div>
          <div className="font-display text-2xl text-navy leading-tight">{de ? "Inklusivleistungen während Ihrer Abwesenheit" : "Included services during your absence"}</div>
          <p className="text-[12px] text-navy-dark/60 mt-1">
            {de ? "Alle Services, die in Ihrer Abwesenheit enthalten sind" : "All services included during your absence"}
          </p>
        </div>
        <span className="text-gold text-xl transition-transform group-hover:translate-x-1" style={{ color: BRAND_COLORS.gold }}>→</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.75)}` }}
          data-testid="my-services-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-ivory max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-gold/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 border-b border-navy/10 flex items-start justify-between gap-4 sticky top-0 bg-ivory z-10">
              <div>
                <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Weissblom</div>
                <h2 className="font-display text-3xl text-navy">{de ? "Mein Leistungsumfang" : "My service scope"}</h2>
                <p className="text-[13px] text-navy-dark/70 mt-2 leading-relaxed max-w-md">
                  {de
                    ? "Diese Services sind in Ihrer persönlichen Betreuung enthalten. Sprechen Sie uns bei Fragen jederzeit an."
                    : "These services are included in your personal care package. Reach out any time if you have questions."}
                </p>
                <p className="text-[13px] text-navy-dark/70 mt-2 leading-relaxed max-w-md">
                  {de ? (
                    <>Nicht gewünschte Inklusivleistungen sind mit Ihrer Buchung im Bereich <b>Meine Abwesenheit vorbereiten</b> abwählbar.</>
                  ) : (
                    <>Unwanted included services can be deselected with your booking in the <b>Prepare my absence</b> section.</>
                  )}
                </p>
              </div>
              <button
                data-testid="my-services-close"
                onClick={() => setOpen(false)}
                className="label-caps font-bold border rounded px-3 py-1.5 transition-colors hover:bg-[#1F4B7A]/10"
                style={{ color: "#1F4B7A", borderColor: "#1F4B7A", fontWeight: 700 }}
              >
                {de ? "Schließen" : "Close"}
              </button>
            </div>
            <div className="p-8 space-y-8" data-testid="my-services-groups">
              {(groups || [{ category: de ? "Leistungen" : "Services", items: flat }]).map((g) => (
                <section key={g.category} data-testid={`services-group-${g.category}`}>
                  <div className="flex items-baseline gap-4 mb-4">
                    <h3 className="font-display text-xl text-navy">{g.category}</h3>
                    <div className="flex-1 border-b border-gold/40" />
                    <span className="label-caps text-navy-dark/50">{g.items.length}</span>
                  </div>
                  <ul className="space-y-3" data-testid={`services-list-${g.category}`}>
                    {g.items.map((f) => {
                      running += 1;
                      return (
                        <li key={f} className="flex items-start gap-4 pb-3 border-b border-navy/10 last:border-b-0">
                          <span className="font-display text-lg leading-none pt-1 w-6" style={{ color: BRAND_COLORS.gold }}>{String(running).padStart(2, "0")}</span>
                          <span className="text-[14px] text-navy-dark leading-relaxed">{f}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
